import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Label,
  LoadingState,
  Select,
  Textarea,
} from '@/components/ui/primitives';
import {
  ConfirmButton,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/overlays';
import { formatCents, formatLocalDate, parseCurrencyToCents, todayLocalDate } from '@/lib/utils';
import { HOSTING_STATUSES, PAYMENT_TYPES, WEBSITE_PROJECT_STATUSES } from '@shared/enums.js';
import type { LeadDetail } from '@shared/types.js';

/**
 * [v5] The linked customer record is reachable from the lead detail page from
 * Slice D onward, so converted customers are never invisible.
 */
export function CustomerPanel({ leadId, lead }: { leadId: number; lead: LeadDetail }) {
  const queryClient = useQueryClient();
  const [convertOpen, setConvertOpen] = React.useState(false);
  const [paymentOpen, setPaymentOpen] = React.useState(false);

  const customer = useQuery({ queryKey: ['lead-customer', leadId], queryFn: () => api.leadCustomer(leadId) });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lead-customer', leadId] });
    queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const updateCustomer = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Record<string, unknown> }) => api.updateCustomer(id, patch),
    onSuccess: () => {
      invalidate();
      toast.success('Customer updated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deletePayment = useMutation({
    mutationFn: (id: number) => api.deletePayment(id),
    onSuccess: () => {
      invalidate();
      toast.success('Payment removed');
    },
  });

  if (customer.isLoading) return <LoadingState />;

  const record = customer.data?.customer;
  const payments = customer.data?.payments ?? [];

  if (!record) {
    return (
      <>
        <EmptyState
          title="Not a customer yet"
          description={
            lead.lead.pipelineStatus === 'Sold'
              ? 'This lead is marked Sold. Convert it to record the sale and track payments.'
              : 'Convert this lead once the sale is closed. The lead record is kept and linked.'
          }
          action={
            <Button size="sm" onClick={() => setConvertOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Convert to customer
            </Button>
          }
        />
        <ConvertDialog open={convertOpen} onOpenChange={setConvertOpen} leadId={leadId} onDone={invalidate} />
      </>
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>Sale</CardTitle>
          <Badge variant="success">Customer #{record.id}</Badge>
        </CardHeader>
        <CardBody className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div>
            <Label>Service sold</Label>
            <p className="text-sm">{record.serviceSold}</p>
          </div>
          <div>
            <Label>Sale amount</Label>
            <p className="text-sm font-semibold tabular-nums">{formatCents(record.saleAmountCents)}</p>
          </div>
          <div>
            <Label>Sale date</Label>
            <p className="text-sm">{formatLocalDate(record.saleDate)}</p>
          </div>
          <div>
            <Label>Domain</Label>
            <p className="truncate text-sm">{record.domainName ?? '—'}</p>
          </div>

          <Field label="Project status">
            <Select
              value={record.websiteProjectStatus}
              onChange={(e) =>
                updateCustomer.mutate({ id: record.id, patch: { websiteProjectStatus: e.target.value } })
              }
            >
              {WEBSITE_PROJECT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Hosting">
            <Select
              value={record.hostingStatus}
              onChange={(e) => updateCustomer.mutate({ id: record.id, patch: { hostingStatus: e.target.value } })}
            >
              {HOSTING_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
          <div>
            <Label>Renewal date</Label>
            <p className="text-sm">{record.renewalDate ? formatLocalDate(record.renewalDate) : '—'}</p>
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardBody>
            <p className="section-label">Booked</p>
            <p className="mt-0.5 text-lg font-semibold tabular-nums">{formatCents(record.saleAmountCents)}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="section-label">Collected</p>
            <p className="mt-0.5 text-lg font-semibold tabular-nums text-emerald-300">
              {formatCents(record.collectedCents ?? 0)}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="section-label">Outstanding</p>
            <p
              className={`mt-0.5 text-lg font-semibold tabular-nums ${
                (record.outstandingCents ?? 0) > 0 ? 'text-amber-300' : ''
              }`}
            >
              {formatCents(record.outstandingCents ?? 0)}
            </p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Payments</CardTitle>
          <Button variant="outline" size="xs" onClick={() => setPaymentOpen(true)}>
            <Plus className="h-3 w-3" />
            Record payment
          </Button>
        </CardHeader>
        <CardBody>
          {payments.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No payments recorded. The deposit is simply the first payment entered here — it is never stored
              separately, so it cannot be double counted.
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th className="text-right">Amount</th>
                  <th>Notes</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td className="whitespace-nowrap">{formatLocalDate(p.paymentDate)}</td>
                    <td>
                      <Badge variant={p.amountCents < 0 ? 'danger' : 'outline'}>{p.paymentType}</Badge>
                    </td>
                    <td
                      className={`text-right font-medium tabular-nums ${
                        p.amountCents < 0 ? 'text-red-300' : 'text-emerald-300'
                      }`}
                    >
                      {formatCents(p.amountCents)}
                    </td>
                    <td className="text-muted-foreground">{p.notes ?? '—'}</td>
                    <td>
                      <ConfirmButton
                        title="Remove this payment?"
                        description="Collected and outstanding totals will be recalculated."
                        confirmLabel="Remove"
                        onConfirm={() => deletePayment.mutate(p.id)}
                      >
                        <button className="text-muted-foreground hover:text-red-300">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </ConfirmButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <PaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        customerId={record.id}
        isFirst={payments.length === 0}
        onDone={invalidate}
      />
    </div>
  );
}

function ConvertDialog({
  open,
  onOpenChange,
  leadId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: number;
  onDone: () => void;
}) {
  const [serviceSold, setServiceSold] = React.useState('Website build');
  const [amount, setAmount] = React.useState('');
  const [saleDate, setSaleDate] = React.useState(todayLocalDate());
  const [domainName, setDomainName] = React.useState('');
  const [notes, setNotes] = React.useState('');

  const convert = useMutation({
    mutationFn: () =>
      api.createCustomer({
        leadId,
        serviceSold: serviceSold.trim(),
        saleAmountCents: parseCurrencyToCents(amount),
        saleDate,
        domainName: domainName.trim() || null,
        customerNotes: notes.trim() || null,
      }),
    onSuccess: () => {
      onDone();
      onOpenChange(false);
      toast.success('Converted to customer');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Convert to customer</DialogTitle>
          <DialogDescription>
            The lead record is kept and linked. Payments are recorded separately, so a deposit is never counted twice.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="Service sold">
            <Input value={serviceSold} onChange={(e) => setServiceSold(e.target.value)} autoFocus />
          </Field>
          <Field label="Sale amount" hint="The full agreed price, not the deposit.">
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$2,500.00" />
          </Field>
          <Field label="Sale date">
            <Input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} />
          </Field>
          <Field label="Domain (optional)">
            <Input value={domainName} onChange={(e) => setDomainName(e.target.value)} />
          </Field>
          <Field label="Notes">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!serviceSold.trim() || parseCurrencyToCents(amount) <= 0}
            loading={convert.isPending}
            onClick={() => convert.mutate()}
          >
            Convert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({
  open,
  onOpenChange,
  customerId,
  isFirst,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: number;
  isFirst: boolean;
  onDone: () => void;
}) {
  const [amount, setAmount] = React.useState('');
  const [paymentDate, setPaymentDate] = React.useState(todayLocalDate());
  const [paymentType, setPaymentType] = React.useState(isFirst ? 'Deposit' : 'Installment');
  const [notes, setNotes] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setAmount('');
      setPaymentDate(todayLocalDate());
      setPaymentType(isFirst ? 'Deposit' : 'Installment');
      setNotes('');
    }
  }, [open, isFirst]);

  const isRefund = paymentType === 'Refund';

  const add = useMutation({
    mutationFn: () => {
      const cents = Math.abs(parseCurrencyToCents(amount));
      return api.addPayment(customerId, {
        amountCents: isRefund ? -cents : cents,
        paymentDate,
        paymentType,
        notes: notes.trim() || null,
      });
    },
    onSuccess: () => {
      onDone();
      onOpenChange(false);
      toast.success(isRefund ? 'Refund recorded' : 'Payment recorded');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{isRefund ? 'Record a refund' : 'Record a payment'}</DialogTitle>
          <DialogDescription>
            Refunds are stored as negative amounts, so collected totals stay correct.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="Type">
            <Select value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
              {PAYMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Amount" hint={isRefund ? 'Enter a positive amount; it is stored as a refund.' : undefined}>
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$500.00" autoFocus />
          </Field>
          <Field label="Date">
            <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
          </Field>
          <Field label="Notes">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={parseCurrencyToCents(amount) === 0}
            loading={add.isPending}
            onClick={() => add.mutate()}
          >
            {isRefund ? 'Record refund' : 'Record payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
